import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, COMPANY } from "@/lib/apiClient";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [saving, setSaving] = useState(false);

  const changePin = async () => {
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPin, new_password: newPin });
      toast.success("Password berhasil diubah");
      setOpen(false);
      setOldPin("");
      setNewPin("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan");
    } finally {
      setSaving(false);
    }
  };

  const isUserAccount = true;

  return (
    <div className="min-h-screen relative" data-testid="app-shell">
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
      <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-blue-700/10 blur-[150px] pointer-events-none" />

      <header className="sticky top-0 z-30 glass border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/">
            <Logo size={44} />
          </Link>
          <div className="flex items-center gap-3">
            <span
              data-testid="current-role-badge"
              className="hidden sm:inline-flex items-center rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300"
            >
              {user?.label}
            </span>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="open-change-pin-btn"
                  variant="outline"
                  className="rounded-full border-white/15 bg-slate-800/50 hover:bg-slate-700/60"
                >
                  <KeyRound className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{isUserAccount ? "Ubah Password" : "Ubah PIN"}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border-white/10" data-testid="change-pin-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display">
                    {isUserAccount ? "Ubah Password" : "Ubah PIN"}
                  </DialogTitle>
                  <DialogDescription className="text-slate-400">
                    {isUserAccount
                      ? "Masukkan password lama dan password baru (min 6 karakter)."
                      : "Masukkan PIN lama dan PIN baru 6 digit angka."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <Input
                    data-testid="old-pin-input"
                    type="password"
                    placeholder={isUserAccount ? "Password lama" : "PIN lama"}
                    value={oldPin}
                    onChange={(e) => setOldPin(e.target.value)}
                    className="bg-slate-900/70 border-white/10 h-11"
                  />
                  <Input
                    data-testid="new-pin-input"
                    type="password"
                    placeholder={isUserAccount ? "Password baru (min 6 karakter)" : "PIN baru (6 digit)"}
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                    className="bg-slate-900/70 border-white/10 h-11"
                  />
                  <Button
                    data-testid="save-pin-btn"
                    onClick={changePin}
                    disabled={saving}
                    className="w-full h-11 bg-blue-600 hover:bg-blue-500"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isUserAccount ? "Simpan Password" : "Simpan PIN"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              variant="outline"
              className="rounded-full border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Keluar</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-8 py-8 md:py-12">
        {children}
      </main>

      <footer className="relative z-10 border-t border-white/5 mt-10 py-8 text-center text-xs text-slate-500 px-5">
        {COMPANY.address} · {COMPANY.phone}
      </footer>
    </div>
  );
};
